import React from "react";
import Tabs from "@/components/Tabs";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import { Outlet, useLocation } from "react-router-dom";
import useCapabilities from "@/hooks/useCapabilities";
import useEffectiveDisplayUser from "@/hooks/useEffectiveDisplayUser";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS } from "./_utils/amplitude";

const tabs = ["my-tasks", "department-tasks"] as const;
type TabType = (typeof tabs)[number];

const TAB_ORDER: Record<TabType, number> = {
  "my-tasks": 0,
  "department-tasks": 1
};

const TAB_OPTIONS: Record<TabType, { label: string; path: Path }> = {
  "my-tasks": { label: "My Tasks", path: "/tasks/my-tasks" },
  "department-tasks": { label: "Department Tasks", path: "/tasks/department-tasks" }
};

const DEFAULT_TAB_PATH = TAB_OPTIONS["my-tasks"].path;

export default function TasksTabs() {
  const location = useLocation();
  const { pathname, search } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();
  const { loaded } = useCapabilities();
  const user = useEffectiveDisplayUser();
  const { trackEvent } = useAmplitude();
  const hasDepartment = !!user?.department;

  const filteredTabItems = Object.entries(TAB_OPTIONS)
    .filter(([key]) => key !== "department-tasks" || hasDepartment)
    .sort(([keyA], [keyB]) => TAB_ORDER[keyA as TabType] - TAB_ORDER[keyB as TabType])
    .map(([_, tab]) => ({
      label: tab.label,
      active: currentUrl.startsWith(tab.path),
      onClick: () => {
        trackEvent(TASK_ANALYTICS_EVENTS.tabSelected, {
          fromPath: currentUrl,
          toPath: tab.path,
          tab: tab.label
        });
        navigate(`${tab.path}${search}` as Path);
      }
    }));

  React.useEffect(() => {
    if (loaded && currentUrl === "/tasks") {
      navigate(`${DEFAULT_TAB_PATH}${search}` as Path, { replace: true });
    }
  }, [loaded, currentUrl, navigate, search]);

  return (
    <div>
      <Tabs tabItems={filteredTabItems} />
      <Outlet />
    </div>
  );
}
